import { useRef } from "react";
import styled, { keyframes } from "styled-components";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
} from "framer-motion";
import { useTranslation } from "react-i18next";

// ─── Data ─────────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 0,
    number: "01",
    title: "First Contact",
    titleItalic: "& Consultation",
    description:
      "We start with a relaxed conversation — about your vision, your skin, your occasion. No pressure, just listening. This is where I get to know you.",
    detail: "Free · 30 min · Via call or in person",
    icon: "✦",
  },
  {
    id: 1,
    number: "02",
    title: "The",
    titleItalic: "Trial",
    description:
      "For bridal and editorial clients, we test every product on your skin, refine the look together, and make sure you feel completely at home in it.",
    detail: "2h session · Studio or on-location",
    icon: "◈",
  },
  {
    id: 2,
    number: "03",
    title: "The Day",
    titleItalic: "of",
    description:
      "I arrive early, work with calm precision, and stay until you're ready. The atmosphere I create is as important as the look itself — unhurried, warm, yours.",
    detail: "Day-of · Full session included",
    icon: "◇",
  },
  {
    id: 3,
    number: "04",
    title: "After",
    titleItalic: "Care",
    description:
      "You leave with a personalised product list, tips for touch-ups, and my direct line for questions. The relationship doesn't end when the brush is down.",
    detail: "Product guide · Ongoing support",
    icon: "○",
  },
];

// ─── Styled ───────────────────────────────────────────────────────────
const Section = styled.section`
  position: relative;
  width: 100%;
  background: var(--cream);
  padding: 10rem 0 12rem;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.035'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }
`;

const TopRule = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 7vw;
  right: 7vw;
  height: 1px;
  background: linear-gradient(
    90deg,
    var(--terracotta) 0%,
    var(--gold) 40%,
    transparent 100%
  );
  opacity: 0.2;
  transform-origin: left;
`;

const Watermark = styled(motion.span)`
  position: absolute;
  top: 6rem;
  left: -2vw;
  font-family: var(--font-serif);
  font-size: clamp(10rem, 18vw, 22rem);
  font-weight: 300;
  font-style: italic;
  color: transparent;
  -webkit-text-stroke: 1px rgba(122, 92, 71, 0.05);
  line-height: 1;
  pointer-events: none;
  user-select: none;
  z-index: 0;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 7vw;
`;

// ── Header ────────────────────────────────────────────────────────────
const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
  gap: 2rem;
  margin-bottom: 8rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    margin-bottom: 5rem;
  }
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.4rem;
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 36px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.8;
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.63rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;
const HeadingWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.15rem;
`;
const Heading = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(2.8rem, 4.5vw, 4.4rem);
  font-weight: 300;
  line-height: 0.95;
  color: var(--ink);
  letter-spacing: -0.01em;
`;
const HeadingItalic = styled(Heading)`
  font-style: italic;
  color: var(--terracotta);
`;
const HeaderDesc = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.82rem;
  font-weight: 200;
  letter-spacing: 0.06em;
  line-height: 1.85;
  color: var(--ink-soft);
  max-width: 36ch;
  align-self: end;
`;

// ── Steps layout ──────────────────────────────────────────────────────
const StepsWrap = styled.div`
  position: relative;
`;

// The vertical spine line that runs through all steps
const SpineLine = styled.div`
  position: absolute;
  left: calc(50% - 0.5px);
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(122, 92, 71, 0.1);
  z-index: 0;

  @media (max-width: 800px) {
    left: 2rem;
  }
`;

// Animated fill on the spine
const SpineFill = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, var(--terracotta) 0%, var(--gold) 100%);
  transform-origin: top;
`;

// Each step row alternates left/right
const StepRow = styled(motion.div)`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 80px 1fr;
  align-items: start;
  margin-bottom: 0;
  min-height: 260px;

  @media (max-width: 800px) {
    grid-template-columns: 260px 1fr;
    min-height: auto;
    margin-bottom: 4rem;
  }
`;

// Left content cell
const CellLeft = styled.div`
  padding-right: 4rem;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 800px) {
    /* display: none; */
    padding-left: 2em;
    width: 300px;
  }
`;

// Right content cell
const CellRight = styled.div`
  padding-left: 4rem;

  @media (max-width: 800px) {
    padding-left: 2rem;
  }
`;

// Empty cell (the other side when content is shown)
const CellEmpty = styled.div``;

// Center spine node
const SpineNode = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 1.2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 800px) {
    padding-top: 0.4rem;
  }
`;

const NodeOuter = styled(motion.div)`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid rgba(122, 92, 71, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cream);
  position: relative;
  flex-shrink: 0;

  &::before {
    content: "";
    position: absolute;
    inset: 5px;
    border-radius: 50%;
    border: 1px solid rgba(122, 92, 71, 0.15);
    transition: border-color 0.4s;
  }
`;

const NodeDot = styled(motion.div)`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--terracotta);
`;

const NodeNumber = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.55rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  color: var(--warm-tan);
  margin-top: 0.7rem;
  text-align: center;
`;

// Connecting segment below each node (except last)
const ConnectorSegment = styled.div`
  flex: 1;
  width: 1px;
  min-height: 180px;
  background: transparent;
  position: relative;
  margin-top: 0.5rem;
  overflow: visible;

  @media (max-width: 800px) {
    min-height: 80px;
  }
`;

const ConnectorFill = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, var(--terracotta), var(--gold));
  transform-origin: top;
`;

// Step content card
const StepCard = styled(motion.div)`
  background: transparent;
  padding: 0 0 4rem 0;
`;

const StepIcon = styled(motion.div)`
  font-size: 1.1rem;
  color: var(--terracotta);
  opacity: 0.7;
  margin-bottom: 1.2rem;
  display: block;
`;

const StepTitleWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.1rem;
`;
const StepTitle = styled(motion.h3)`
  font-family: var(--font-serif);
  font-size: clamp(2rem, 3vw, 2.8rem);
  font-weight: 300;
  line-height: 0.92;
  color: var(--ink);
  letter-spacing: -0.01em;
`;
const StepTitleItalic = styled(StepTitle)`
  font-style: italic;
  color: var(--terracotta);
`;

const StepDivider = styled(motion.div)`
  width: 28px;
  height: 1px;
  background: var(--warm-tan);
  opacity: 0.5;
  margin: 1.4rem 0 1.6rem;
`;

const StepDesc = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.8rem;
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.9;
  color: var(--ink-soft);
  max-width: 34ch;
  margin-bottom: 1.4rem;
`;

const StepDetail = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--warm-tan);
  display: block;
`;

// ── Bottom CTA ────────────────────────────────────────────────────────
const BottomCta = styled.div`
  margin-top: 8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 2rem;
  text-align: center;
`;

const CtaHeading = styled(motion.p)`
  font-family: var(--font-serif);
  font-size: clamp(1.4rem, 2.5vw, 2.2rem);
  font-weight: 300;
  font-style: italic;
  color: var(--ink-soft);
  line-height: 1.4;
`;

const CtaBtn = styled(motion.button)`
  font-family: var(--font-sans);
  font-size: 0.72rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--cream);
  background: var(--ink);
  border: none;
  padding: 1.2rem 3.2rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--terracotta);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    z-index: 0;
  }
  &:hover::after {
    transform: scaleX(1);
  }
  span {
    position: relative;
    z-index: 1;
  }
`;

// ─── Variants ─────────────────────────────────────────────────────────
const revealUp = {
  hidden: { y: "105%" },
  show: { y: "0%", transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};

const STEP_CONFIG = [
  { id: 0, number: "01", icon: "✦" },
  { id: 1, number: "02", icon: "◈" },
  { id: 2, number: "03", icon: "◇" },
  { id: 3, number: "04", icon: "○" },
];

// ─── Single Step Component ──────────────────────────────────────────
function Step({ config, index, isLast }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15% 0px" });
  const isEven = index % 2 === 0;

  const cardVariant = {
    hidden: { opacity: 0, x: isEven ? 40 : -40, y: 10 },
    show: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
    },
  };
  const content = (
    <StepCard
      ref={ref}
      variants={cardVariant}
      initial="hidden"
      animate={isInView ? "show" : "hidden"}
    >
      <StepIcon
        initial={{ opacity: 0, scale: 0.5 }}
        animate={isInView ? { opacity: 0.7, scale: 1 } : {}}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        {config.icon}
      </StepIcon>

      <StepTitleWrap>
        <StepTitle
          initial={{ y: "105%" }}
          animate={isInView ? { y: "0%" } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {t(`process.steps.${index}.title`)}
        </StepTitle>
      </StepTitleWrap>
      <StepTitleWrap>
        <StepTitleItalic
          as="h3"
          initial={{ y: "105%" }}
          animate={isInView ? { y: "0%" } : {}}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          {t(`process.steps.${index}.titleItalic`)}
        </StepTitleItalic>
      </StepTitleWrap>

      <StepDivider
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ delay: 0.45, duration: 0.6, ease: "easeOut" }}
        style={{ transformOrigin: "left" }}
      />

      <StepDesc
        initial={{ opacity: 0, y: 12 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        {t(`process.steps.${index}.desc`)}
      </StepDesc>

      <StepDetail
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 0.65, duration: 0.7 }}
      >
        {t(`process.steps.${index}.detail`)}
      </StepDetail>
    </StepCard>
  );

  return (
    <StepRow>
      {isEven ? <CellEmpty /> : <CellLeft>{content}</CellLeft>}
      <SpineNode>
        <NodeOuter
          initial={{ scale: 0, opacity: 0 }}
          animate={isInView ? { scale: 1, opacity: 1 } : {}}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 18,
            delay: 0.05,
          }}
        >
          <NodeDot
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 16,
              delay: 0.2,
            }}
          />
        </NodeOuter>
        <NodeNumber
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
        >
          {config.number}
        </NodeNumber>

        {!isLast && (
          <ConnectorSegment>
            <ConnectorFill
              initial={{ scaleY: 0 }}
              animate={isInView ? { scaleY: 1 } : {}}
              transition={{
                duration: 1.1,
                delay: 0.4,
                ease: [0.22, 1, 0.36, 1],
              }}
            />
          </ConnectorSegment>
        )}
      </SpineNode>
      {isEven ? <CellRight>{content}</CellRight> : <CellEmpty />}
    </StepRow>
  );
}

// ─── Main Section Component ──────────────────────────────────────────
export default function ProcessSection() {
  const { t } = useTranslation();
  const headerRef = useRef(null);
  const ctaRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-10%" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-10%" });

  return (
    <Section>
      <TopRule
        initial={{ scaleX: 0 }}
        animate={headerInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      />

      <Watermark
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.4 }}
      >
        {t("process.watermark")}
      </Watermark>

      <Inner>
        <Header ref={headerRef}>
          <div>
            <EyebrowRow>
              <EyebrowDash
                initial={{ scaleX: 0 }}
                animate={headerInView ? { scaleX: 1 } : {}}
                transition={{ delay: 0.2, duration: 0.6 }}
                style={{ transformOrigin: "left" }}
              />
              <Eyebrow
                initial={{ opacity: 0, x: -10 }}
                animate={headerInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.35, duration: 0.6 }}
              >
                {t("process.eyebrow")}
              </Eyebrow>
            </EyebrowRow>

            <HeadingWrap>
              <Heading
                variants={revealUp}
                initial="hidden"
                animate={headerInView ? "show" : "hidden"}
                transition={{ delay: 0.3 }}
              >
                {t("process.heading_main")}
              </Heading>
            </HeadingWrap>
            <HeadingWrap>
              <HeadingItalic
                as={motion.h2}
                variants={revealUp}
                initial="hidden"
                animate={headerInView ? "show" : "hidden"}
                transition={{ delay: 0.42 }}
              >
                {t("process.heading_italic")}
              </HeadingItalic>
            </HeadingWrap>
          </div>

          <HeaderDesc
            initial={{ opacity: 0, y: 16 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.55, duration: 0.8 }}
          >
            {t("process.header_desc")}
          </HeaderDesc>
        </Header>

        <StepsWrap>
          <SpineLine />
          {STEP_CONFIG.map((config, i) => (
            <Step
              key={config.id}
              config={config}
              index={i}
              isLast={i === STEP_CONFIG.length - 1}
            />
          ))}
        </StepsWrap>

        {/* <BottomCta ref={ctaRef}>
          <CtaHeading
            initial={{ opacity: 0, y: 16 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {t("process.cta_text")}
          </CtaHeading>
          <CtaBtn
            initial={{ opacity: 0, y: 12 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.7 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            <span>{t("process.cta_btn")}</span>
          </CtaBtn>
        </BottomCta> */}
      </Inner>
    </Section>
  );
}
